import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Textarea from "@mui/joy/Textarea";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import Card from "@mui/joy/Card";
import Grid from '@mui/joy/Grid';
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useSearchParams } from 'react-router-dom';
import PageBreadcrumbs from "../components/common/PageBreadcrumbs";

dayjs.extend(utc);
dayjs.extend(timezone);

import CircularProgress from '@mui/joy/CircularProgress';
import { TagRounded, CurrencyRupeeRounded, DownloadRounded } from "@mui/icons-material";

import Sidebar from "../components/common/Sidebar";
import Header from "../components/common/Header";
import JoyDatePicker from "../components/common/JoyDatePicker";

type DonationCartItem = {
   id: number;
   donationId: number;
   cartId: string;
   itemId: string;
   name: string;
   amount: number;
   quantity: number;
   pagePath: string;
   lineTotal: number;
   createdAt: string;
};

export default function DonationDetails() {
   const [searchParams] = useSearchParams();
   const donationId = searchParams.get("id");
   const [messageD, setMessageD] = useState<string | null>(null);

   let navigate = useNavigate();

   const [donationData, setDonationData] = useState({
      id: "",
      donorId: "",
      name: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      pan: "",
      email: "",
      donorType: "individual",
      date: "",
      amount: "",
      paymentMethod: "",
      gift: "",
      purpose: "",
      event: "",
      taxReceiptNo: "",
      head: "",
      status: "",
      note: "",
      createdBy: "",
      updatedBy: "",
      createdAt: "",
      updatedAt: "",
      txnRefId: "",
      refBy: null,
      subPurpose: "",
      cartId: "",
      cartItems: [] as DonationCartItem[],
   });

   const [isDeleting, setIsDeleting] = useState(false);
   const [loading, setLoading] = useState(true);
   const [options, setOptions] = useState({
      Purpose: [],
      Event: [],
      Head: [],
      Gift: [],
   });
   const [confirmDelete, setConfirmDelete] = useState(false);

   const handleDeleteDonation = async () => {
      setIsDeleting(true);
      try {
         const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donations/${donationId}`, {
            method: 'DELETE',
            credentials: 'include',
         });

         if (response.ok) {
            navigate("/donation");
         } else {
            if (response.status === 401) {
               const redirectUrl = encodeURIComponent(window.location.href);
               window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
               return;
            }
            throw new Error("Failed to delete donation");
         }
      } catch (err) {
         console.error("Error deleting donation:", err);
      } finally {
         setIsDeleting(false);
         setConfirmDelete(false);
      }
   };

   useEffect(() => {
      const fetchDonationDetails = async () => {
         if (!donationId) return;

         try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation/${donationId}`, {
               credentials: "include",
            });

            if (!response.ok) {
               if (response.status === 401) {
                  const redirectUrl = encodeURIComponent(window.location.href);
                  window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
                  return;
               }
               throw new Error("Failed to fetch donation details");
            }

            const data = await response.json();

            setDonationData({
               id: data.id,
               donorId: data.donorId?.toString() || "",
               name: data.name || "",
               phone: data.phone || "",
               address: data.address || "",
               city: data.city || "",
               state: data.state || "",
               country: data.country || "India",
               pincode: data.pincode || "",
               pan: data.pan || "",
               email: data.email || "",
               donorType: data.donorType || "individual",
               date: data.date,
               amount: data.amount.toString(),
               paymentMethod: data.paymentMethod,
               gift: data.gift || "",
               purpose: data.purpose || "",
               event: data.event || "",
               taxReceiptNo: data.taxReceiptNo || "",
               head: data.head || "",
               status: data.status || "",
               note: data.note || "",
               createdBy: data.createdBy || "",
               updatedBy: data.updatedBy || "",
               createdAt: data.createdAt || "",
               updatedAt: data.updatedAt || "",
               txnRefId: data.txnRefId || "",
               refBy: data.refBy || null,
               subPurpose: data.subPurpose || "",
               cartId: data.cartId || "",
               cartItems: Array.isArray(data.cartItems) ? data.cartItems : [],
            });

            setLoading(false);
         } catch (error) {
            console.error("Error fetching donation details:", error);
            setMessageD("Can't find the donation details for this ID.")
            setLoading(false);
         }
      };

      fetchDonationDetails();
   }, [donationId]);

   useEffect(() => {
      const fetchOptions = async () => {
         const categories = ["Purpose", "Event", "Head", "Gift"];
         try {
            const results = await Promise.all(
               categories.map((type) =>
                  fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/options?type=${type}`, {
                     credentials: "include",
                  }).then((res) => res.json())
               )
            );
            const data: any = {};
            categories.forEach((cat, idx) => {
               data[cat] = results[idx];
            });
            setOptions(data);
         } catch (error) {
            console.error("Failed to fetch donation options:", error);
         }
      };

      fetchOptions();
   }, []);


   return (
      <Box sx={{ display: "flex", minHeight: "100dvh" }}>
         <Header />
         <Sidebar />
         <Box
            component="main"
            className="MainContent"
            sx={{
               pt: { xs: "calc(12px + var(--Header-height))", md: 3 },
               pb: { xs: 2, sm: 2, md: 3 },
               flex: 1,
               display: "flex",
               flexDirection: "column",
               minWidth: 0,
               gap: 1,
               overflow: "auto",
               bgcolor: "background.body",
            }}
         >
            <Box sx={{ flex: 1, width: "100%" }}>
               <Box
                  sx={{
                     position: "sticky",
                     top: { sm: -100, md: -110 },
                     bgcolor: "background.body",
                     zIndex: 9995,
                  }}
               >
                  <Box sx={{ px: { xs: 2, md: 6 } }}>
                     <PageBreadcrumbs
                        links={[
                           { label: "Donation", href: "/donation" },
                        ]}
                        current={`ID - ${donationId}`}
                     />
                     <Typography level="h2" component="h1" sx={{ mt: 1, mb: 2 }}>
                        Donation Details
                     </Typography>
                  </Box>
               </Box>
               <Stack
                  spacing={4}
                  sx={{
                     display: "flex",
                     maxWidth: "800px",
                     mx: "auto",
                     px: { xs: 2, md: 6 },
                     py: { xs: 2, md: 2 },
                  }}
               >
                  <Card>
                     <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                           <Typography level="title-md">Donation Details</Typography>
                           <Typography level="body-sm">
                              Find all the donation details for Donation ID - {donationId}.
                           </Typography>
                        </Box>
                        <Button
                           size="sm"
                           variant="solid"
                           color="primary"
                           startDecorator={<DownloadRounded />}
                           onClick={async () => {
                              try {
                                 const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation/receipt/${donationId}`, {
                                    method: 'GET',
                                    credentials: 'include'
                                 });
                                 if (!response.ok) throw new Error('Failed to download receipt');

                                 const blob = await response.blob();
                                 const url = window.URL.createObjectURL(blob);
                                 const link = document.createElement('a');
                                 link.href = url;
                                 link.setAttribute('download', `donation-receipt-${donationId}.pdf`);
                                 document.body.appendChild(link);
                                 link.click();
                                 link.remove();
                              } catch (err) {
                                 console.error('Error downloading receipt:', err);
                              }
                           }}
                        >
                           Download Receipt
                        </Button>
                     </Box>
                     {/* Doner Profile */}

                     {(loading) ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 10 }}>
                           <CircularProgress size="md" />
                        </Box>
                     ) : (
                        (donationData.id) ? (
                           <Box>
                              <Grid container spacing={2} sx={{ flexGrow: 1, my: 1 }}>
                                 <Grid xs={12} sm={1}>
                                    <FormControl>
                                       <FormLabel >ID</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="ID"
                                          name="id"
                                          readOnly
                                          value={donationData.id}

                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={4}>
                                    <FormControl sx={{ flexGrow: 2 }}>
                                       <FormLabel >Name</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="Full name"
                                          name="name"
                                          value={donationData.name}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={3}>
                                    <FormControl>
                                       <FormLabel>Phone</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="e.g., 1234567890"
                                          name="phone"
                                          value={donationData.phone}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={4}>
                                    <FormControl sx={{ flexGrow: 1 }}>
                                       <FormLabel>Email</FormLabel>
                                       <Input
                                          size="sm"
                                          type="email"
                                          placeholder="username@example.com"
                                          name="email"
                                          value={donationData.email}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={8}>
                                    <FormControl sx={{ flexGrow: 1 }}>
                                       <FormLabel>Address</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="e.g., 62/72 Street, Area"
                                          name="address"
                                          value={donationData.address}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={4}>
                                    <FormControl>
                                       <FormLabel>State</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="Select"
                                          name="state"
                                          value={donationData.state}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={4}>
                                    <FormControl>
                                       <FormLabel>City</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="e.g., KANPUR"
                                          name="city"
                                          value={donationData.city}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={2}>
                                    <FormControl>
                                       <FormLabel>Pincode</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="e.g., 000000"
                                          name="pincode"
                                          value={donationData.pincode}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={3}>
                                    <FormControl>
                                       <FormLabel>PAN</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="ABCDE1234F"
                                          name="pan"
                                          value={donationData.pan}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                                 <Grid xs={12} sm={3}>
                                    <FormControl sx={{ flexGrow: 1 }}>
                                       <FormLabel>Type</FormLabel>
                                       <Input
                                          size="sm"
                                          placeholder="Select"
                                          name="donorType"
                                          value={donationData.donorType}
                                          readOnly
                                       />
                                    </FormControl>
                                 </Grid>
                              </Grid>

                              <Box>
                                 <Divider />
                                 <Grid container spacing={2} sx={{ flexGrow: 1, my: 1 }}>
                                    <Grid xs={12} sm={4}>
                                       <FormControl sx={{ flexGrow: 1 }}>
                                          <FormLabel>Amount</FormLabel>
                                          <Input
                                             size="sm"
                                             name="amount"
                                             value={donationData.amount}
                                             startDecorator={<CurrencyRupeeRounded />}
                                             placeholder="e.g., 1,000"
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={4}>
                                       <FormControl sx={{ flexGrow: 1 }}>
                                          <FormLabel>Payment Method</FormLabel>
                                          <Input
                                             size="sm"
                                             placeholder="Select"
                                             name="paymentMethod"
                                             value={donationData.paymentMethod}
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={4}>
                                       <FormControl >
                                          <FormLabel>Date</FormLabel>
                                          <JoyDatePicker
                                             name="date"
                                             value={donationData.date ? dayjs(donationData.date) : null}
                                             readOnly
                                             format="YYYY-MM-DD"
                                             slotProps={{
                                                textField: {
                                                   size: "small",
                                                   sx: { height: "32px", minHeight: "32px", fontSize: "0.875rem", padding: "5px 10px" }, // Ensures consistent height
                                                },
                                             }}
                                             sx={{ height: "32px", minHeight: "32px", "& .MuiInputBase-root": { height: "32px" } }}
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={4}>
                                       <FormControl sx={{ flexGrow: 1 }}>
                                          <FormLabel>Purpose</FormLabel>
                                          <Input
                                             size="sm"
                                             placeholder="Select"
                                             name="purpose"
                                             value={donationData.purpose}
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={4}>
                                       <FormControl sx={{ flexGrow: 1 }}>
                                          <FormLabel>Event</FormLabel>
                                          <Input
                                             size="sm"
                                             placeholder="Select"
                                             name="event"
                                             value={donationData.event}
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={4}>
                                       <FormControl sx={{ flexGrow: 1 }}>
                                          <FormLabel>Tax Receipt No.</FormLabel>
                                          <Input
                                             size="sm"
                                             name="taxReceiptNo"
                                             startDecorator={<TagRounded />}
                                             value={donationData.taxReceiptNo}
                                             placeholder="e.g., 2025068"
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={6}>
                                       <FormControl sx={{ flexGrow: 1 }}>
                                          <FormLabel>Gift</FormLabel>
                                          <Input
                                             size="sm"
                                             placeholder="Select"
                                             name="gift"
                                             value={donationData.gift}
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={6}>
                                       <FormControl sx={{ flexGrow: 1 }}>
                                          <FormLabel>Head</FormLabel>
                                          <Input
                                             size="sm"
                                             placeholder="Select"
                                             name="head"
                                             value={donationData.head}
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={6}>
                                       <FormControl sx={{ flexGrow: 1 }}>
                                          <FormLabel>Cart ID</FormLabel>
                                          <Input
                                             size="sm"
                                             placeholder="-"
                                             name="cartId"
                                             value={donationData.cartId || ""}
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={12}>
                                       <FormControl sx={{ display: { sm: "contents" } }}>
                                          <FormLabel>Note</FormLabel>
                                          <Textarea
                                             size="sm"
                                             minRows={2}
                                             sx={{ mt: 1.5 }}
                                             value={donationData.note || ""}
                                             placeholder="Add a note"
                                             readOnly
                                          />
                                       </FormControl>
                                    </Grid>
                                    <Grid xs={12} sm={12}>
                                       <FormControl sx={{ display: { sm: "contents" } }}>
                                          <FormLabel>Cart Line Items</FormLabel>
                                          <Box sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 'sm', p: 1.5 }}>
                                             {donationData.cartItems.length === 0 ? (
                                                <Typography level="body-sm" color="neutral">No tracked cart items</Typography>
                                             ) : (
                                                donationData.cartItems.map((item) => (
                                                   <Box key={item.id} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                                                      <Typography level="body-sm">
                                                         {item.name || item.itemId} x {item.quantity} = ₹{item.lineTotal}
                                                      </Typography>
                                                      <Typography level="body-xs" color="neutral">
                                                         Item ID: {item.itemId || "-"} | Page: {item.pagePath || "-"} | Unit: ₹{item.amount}
                                                      </Typography>
                                                   </Box>
                                                ))
                                             )}
                                          </Box>
                                       </FormControl>
                                    </Grid>
                                 </Grid>
                                 <CardOverflow sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                                    <Stack
                                       direction="row"
                                       spacing={2}
                                       sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          justifyContent: "space-between",
                                          alignItems: "flex-start",
                                          my: 2,
                                       }}
                                    >
                                       <Box sx={{ mt: 2, px: 1 }}>
                                          <Typography level="body-xs" color="neutral">
                                             Created by: <strong>{donationData.createdBy || "N/A"}</strong> on {donationData.createdAt ? dayjs(donationData.createdAt).format("D MMM YYYY, h:mm A") : "N/A"}
                                          </Typography>
                                          <Typography level="body-xs" color="neutral">
                                             Last updated by: <strong>{donationData.updatedBy || "N/A"}</strong> on {donationData.updatedAt ? dayjs(donationData.updatedAt).format("D MMM YYYY, h:mm A") : "N/A"}
                                          </Typography>
                                       </Box>
                                       <CardActions sx={{ alignSelf: "flex-start", pt: 0 }}>
                                          <Button
                                             size="sm"
                                             variant="outlined"
                                             onClick={() => navigate(`/donation/details/edit?id=${donationId}`)}
                                          >
                                             Edit
                                          </Button>
                                          <Button
                                             size="sm"
                                             variant="outlined"
                                             color="danger"
                                             onClick={() => setConfirmDelete(true)}
                                          >
                                             Delete
                                          </Button>
                                       </CardActions>
                                    </Stack>
                                 </CardOverflow>

                              </Box>
                           </Box>
                        ) : (null)
                     )}
                     {messageD && !donationData.donorId && (
                        <Box sx={{ mt: 2, textAlign: "center" }}>
                           <Typography level="body-sm" sx={{ color: "green", mb: 2 }}>
                              {messageD}
                           </Typography>
                           <Button
                              size="sm"
                              variant="solid"
                              sx={{ my: 5 }}
                              onClick={() => {
                                 navigate("/donation");
                              }}
                           >
                              All Records
                           </Button>
                        </Box>
                     )}
                  </Card>
               </Stack>
            </Box>
            <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} sx={{ zIndex: 9999 }}>
               <ModalDialog variant="outlined" role="alertdialog">
                  <Typography level="h4">Confirm Deletion</Typography>
                  <Typography level="body-sm">Are you sure you want to delete this donation?</Typography>
                  <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                     <Button variant="plain" color="neutral" onClick={() => setConfirmDelete(false)}>Cancel</Button>
                     <Button
                        variant="solid"
                        color="danger"
                        onClick={handleDeleteDonation}
                        disabled={isDeleting}
                     >
                        {isDeleting ? "Deleting..." : "Delete"}
                     </Button>
                  </Box>
               </ModalDialog>
            </Modal>
         </Box>
      </Box>
   );
}

